import React, { useState, useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';

/**
 * Parses an error stack trace to extract important information
 * Extracts the first function name from the stack trace
 */
const parseErrorStack = (error: ErrorBoundaryError) => {
  if (!error.stack) {
    return { filePath: null, errorTitle: null, code: null, firstFilename: null };
  }

  const stack = error.stack;
  const stackLines = stack.split('\n');

  // Extract error message from first line
  const errorMessage = stackLines[0].trim();

  // Extract first function name from the stack trace
  let firstFilename = null;

  // Find the first stack line (starts with " at ")
  for (let i = 1; i < stackLines.length; i++) {
    const line = stackLines[i].trim();
    if (line.startsWith('at ')) {
      // Extract function name pattern
      const match = line.match(/at\s+([^\s(]+)[\s(]/);
      if (match && match[1]) {
        firstFilename = match[1];
        break;
      }
    }
  }

  // Sanitize stack trace for display - safer approach to avoid ReDoS
  const sanitizedStack = stackLines
    .map(line => {
      // Limit line length to prevent excessive processing
      const limitedLine = line.substring(0, 2000);

      // Process each part separately to avoid complex regex patterns
      if (limitedLine.includes('(')) {
        // Extract filename from paths in parentheses
        const openParenIndex = limitedLine.indexOf('(');
        const closeParenIndex = limitedLine.indexOf(')', openParenIndex);

        if (openParenIndex >= 0 && closeParenIndex > openParenIndex) {
          const pathInParens = limitedLine.substring(openParenIndex + 1, closeParenIndex);

          // Find the last segment after slash or backslash
          const lastSlashIndex = Math.max(
            pathInParens.lastIndexOf('/'),
            pathInParens.lastIndexOf('\\')
          );

          if (lastSlashIndex >= 0) {
            const filename = pathInParens.substring(lastSlashIndex + 1);
            return (
              limitedLine.substring(0, openParenIndex + 1) +
              filename +
              limitedLine.substring(closeParenIndex)
            );
          }
        }
      }

      // Handle the "at Function path:line:column" format
      if (limitedLine.includes(' at ')) {
        const atIndex = limitedLine.indexOf(' at ');
        const afterAt = limitedLine.substring(atIndex + 4).trim();

        // Split by whitespace to separate function and path
        const spaceAfterFunc = afterAt.indexOf(' ');

        if (spaceAfterFunc > 0) {
          const funcName = afterAt.substring(0, spaceAfterFunc);
          const path = afterAt.substring(spaceAfterFunc + 1);

          // Check if this is a path with line/column numbers
          if (path.includes(':') && /.*:[0-9]+:[0-9]+/.test(path)) {
            // Find the last segment after slash or backslash
            const lastSlashIndex = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));

            if (lastSlashIndex >= 0) {
              const filename = path.substring(lastSlashIndex + 1);
              return limitedLine.substring(0, atIndex + 4) + funcName + ' ' + filename;
            }
          }
        }
      }

      return limitedLine;
    })
    .join('\n');

  return {
    errorTitle: errorMessage,
    code: sanitizedStack,
    firstFilename: firstFilename,
  };
};

interface ErrorBoundaryError extends Error {
  message: string;
  stack?: string;
}

const getErrorDiagnosticDetails = (error: ErrorBoundaryError | ErrorEvent | XMLHttpRequest | any) => {
  const isXmlHttpRequest = typeof XMLHttpRequest !== 'undefined' && error instanceof XMLHttpRequest;

  if (isXmlHttpRequest) {
    let responseText;
    try {
      // responseText throws when responseType is e.g. 'arraybuffer'. Keep the
      // error boundary itself from creating another UI error while logging.
      responseText = error.responseText;
    } catch {
      responseText = '<unavailable for this responseType>';
    }

    return {
      type: 'XMLHttpRequest',
      status: error.status,
      statusText: error.statusText,
      readyState: error.readyState,
      responseURL: error.responseURL,
      responseText,
      responseType: error.responseType,
    };
  }

  if (error instanceof ErrorEvent) {
    return {
      type: 'ErrorEvent',
      message: error.message,
      filename: error.filename,
      lineno: error.lineno,
      colno: error.colno,
      error: error.error,
    };
  }

  return {
    type: error?.constructor?.name || typeof error,
    message: error?.message,
    stack: error?.stack,
  };
};

enum ShowErrorDetails {
  always = 'always',
  dev = 'dev',
  production = 'production',
}

interface DefaultFallbackProps extends FallbackProps {
  error: ErrorBoundaryError;
  context: string;
  resetErrorBoundary: () => void;
  showErrorDetails?: ShowErrorDetails;
}

interface ErrorBoundaryProps {
  context?: string;
  onReset?: () => void;
  onError?: (error: ErrorBoundaryError, componentStack: string, context: string) => void;
  fallbackComponent?: React.ComponentType<DefaultFallbackProps>;
  children: React.ReactNode;
  fallbackRoute?: string | null;
  isPage?: boolean;
  showErrorDetails?: ShowErrorDetails;
}

const DefaultFallback = ({ error, context }: DefaultFallbackProps) => {
  // RAIOSS: não exibir o erro genérico ("Something went wrong ...") para o
  // usuário. Em vez do toast/diálogo, registramos os detalhes completos no
  // console para diagnóstico e não renderizamos nada na UI.
  useEffect(() => {
    const { errorTitle, code, firstFilename } = parseErrorStack(error);

    /* eslint-disable no-console */
    console.groupCollapsed(
      `%c[RAIOSS] Erro capturado em "${context}": ${errorTitle || error?.message || 'erro desconhecido'}`,
      'color:#dcff5f;font-weight:bold'
    );
    console.error('Context:', context);
    console.error('Details:', getErrorDiagnosticDetails(error));
    console.error('Message:', error?.message);
    if (firstFilename) {
      console.error('Origem:', firstFilename);
    }
    console.error('Error object:', error);
    if (error?.stack) {
      console.error('Stack (raw):', error.stack);
    }
    if (code) {
      console.error('Stack (sanitizado):', code);
    }
    console.groupEnd();
    /* eslint-enable no-console */
  }, [error, context]);

  // Nada é mostrado ao usuário.
  return null;
};

const ErrorBoundary = ({
  context = 'OHIF',
  onReset = () => {},
  onError = _error => {},
  fallbackComponent: FallbackComponent = DefaultFallback,
  children,
  showErrorDetails,
}: ErrorBoundaryProps) => {
  const [error, setError] = useState<ErrorBoundaryError | null>(null);

  const onResetHandler = () => {
    setError(null);
    onReset();
  };

  // Add error event listener to window
  useEffect(() => {
    let errorTimeout: NodeJS.Timeout;

    const handleError = (event: ErrorEvent) => {
      clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => {
        setError(event.error);
        onErrorHandler(event.error, null);
      }, 100);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault();
      clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => {
        setError(event.reason || event);
        onErrorHandler(event.reason || event, null);
      }, 100);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      clearTimeout(errorTimeout);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const onErrorHandler = (
    error: ErrorBoundaryError | ErrorEvent,
    componentStack: string | null
  ) => {
    // RAIOSS: console.debug fica escondido no nível padrão do DevTools. Como o
    // erro não é mais mostrado na UI, o console é o único canal de diagnóstico,
    // então logamos como erro e com o máximo de contexto possível.
    /* eslint-disable no-console */
    console.groupCollapsed(
      `%c[RAIOSS] Error Boundary "${context}"`,
      'color:#dcff5f;font-weight:bold'
    );
    console.error('Context:', context);
    console.error('Details:', getErrorDiagnosticDetails(error));
    console.error('Error:', error);
    if ((error as ErrorBoundaryError)?.message) {
      console.error('Message:', (error as ErrorBoundaryError).message);
    }
    if ((error as ErrorBoundaryError)?.stack) {
      console.error('Stack:', (error as ErrorBoundaryError).stack);
    }
    if (componentStack) {
      console.error('Component stack:', componentStack);
    }
    console.error('URL:', window.location.href);
    console.groupEnd();
    /* eslint-enable no-console */

    onError(error, componentStack || '', context);
  };

  return (
    <ReactErrorBoundary
      fallbackRender={props => (
        <FallbackComponent
          {...props}
          context={context}
          showErrorDetails={showErrorDetails}
        />
      )}
      onReset={onResetHandler}
      onError={(error, info) => onErrorHandler(error as ErrorBoundaryError, info.componentStack)}
    >
      <>
        {children}
        {error && (
          <FallbackComponent
            error={error}
            context={context}
            resetErrorBoundary={() => setError(null)}
            showErrorDetails={showErrorDetails}
          />
        )}
      </>
    </ReactErrorBoundary>
  );
};

export { ErrorBoundary };
